import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import api from '../api/client';
import { adminLandingPath } from '../utils/adminLanding';

/**
 * 관리자 로그인.
 *
 * 흐름:
 *   1) 이메일 + 비번 입력 → POST /admin/login
 *      - 2FA 비활성 admin: { token, admin } → localStorage 저장 후 / 이동 (기존 호환)
 *      - 2FA 활성 admin:   { requireTotp: true, preAuthToken } → 2단계 입력 폼 표시
 *   2) 6자리 TOTP 코드 또는 8자리 백업코드 입력 → POST /admin/login/verify-totp
 *      → { token, admin } 응답으로 정식 로그인 완료
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('credentials'); // 'credentials' | 'totp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [preAuthToken, setPreAuthToken] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const finishLogin = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('adminRole', data.admin.role);
    localStorage.setItem('adminName', data.admin.name);
    if (data.admin.storeId) {
      localStorage.setItem('adminStoreId', data.admin.storeId);
    }
    if (data.admin.storeCode) {
      localStorage.setItem('adminStoreCode', data.admin.storeCode);
    }
    navigate(adminLandingPath(data.admin.role));
  };

  const handleCredentials = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setErrorMsg('이메일과 비밀번호를 입력해주세요.');
      return;
    }
    setErrorMsg('');
    setLoading(true);
    try {
      const res = await api.post('/admin/login', { email, password });
      if (res.data.requireTotp) {
        setPreAuthToken(res.data.preAuthToken);
        setStep('totp');
      } else {
        finishLogin(res.data);
      }
    } catch (err) {
      if (!err?.response) {
        setErrorMsg('서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.');
      } else {
        setErrorMsg('이메일 또는 비밀번호가 일치하지 않습니다.');
      }
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleTotp = async (e) => {
    e.preventDefault();
    if (!code) {
      setErrorMsg('인증 코드를 입력해주세요.');
      return;
    }
    setErrorMsg('');
    setLoading(true);
    try {
      const res = await api.post('/admin/login/verify-totp', {
        preAuthToken,
        code: code.replace(/[-\s]/g, '').trim(),
      });
      finishLogin(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setErrorMsg('인증 코드가 올바르지 않거나 세션이 만료되었습니다.');
      } else {
        setErrorMsg('인증에 실패했습니다. 다시 시도해주세요.');
      }
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const backToCredentials = () => {
    setStep('credentials');
    setPreAuthToken('');
    setCode('');
    setErrorMsg('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Paper sx={{ p: { xs: 3, sm: 4 }, width: { xs: '100%', sm: 380 }, maxWidth: 380, mx: 2, borderRadius: '16px' }} elevation={2}>
        <Typography variant="h5" align="center" fontWeight={700} sx={{ mb: 1 }}>
          Mogoo 관리자
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          {step === 'credentials' ? '관리자 로그인' : '2단계 인증'}
        </Typography>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>
            {errorMsg}
          </Alert>
        )}

        {step === 'credentials' ? (
          <Box component="form" onSubmit={handleCredentials}>
            <TextField
              label="이메일"
              type="email"
              fullWidth
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="비밀번호"
              type="password"
              fullWidth
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
            >
              {loading ? '로그인 중...' : '로그인'}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleTotp}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              인증 앱(Google Authenticator 등)에 표시된 6자리 코드를 입력하세요.
              <br />
              <Typography variant="caption" color="text.secondary">
                * 인증 앱을 분실한 경우 백업코드(8자리)도 입력 가능
              </Typography>
            </Typography>
            <TextField
              label="인증 코드"
              fullWidth
              autoFocus
              slotProps={{ htmlInput: { inputMode: 'text', autoComplete: 'one-time-code', maxLength: 20 } }}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              sx={{ mb: 3 }}
              placeholder="123456"
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mb: 1 }}
            >
              {loading ? '확인 중...' : '확인'}
            </Button>
            <Button
              type="button"
              variant="text"
              fullWidth
              size="small"
              onClick={backToCredentials}
              disabled={loading}
            >
              뒤로가기
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LoginPage;
